import { randomBytes } from 'node:crypto';
import { createServer, type IncomingMessage, type Server, type ServerResponse } from 'node:http';
import type { AddressInfo } from 'node:net';
import {
  LIVE_HTML_CONTRACT_HEADER,
  LIVE_HTML_CONTRACT_VERSION,
  type LiveHtmlEvent,
} from '@okrapdf/schemas/live-html';
import { escapeHtml, htmlToText } from './html';
import type { LiveViewSession } from './session';
import { Data, Effect, Exit, Schedule, type Scope } from 'effect';

export class LiveViewServerError extends Data.TaggedError('LiveViewServerError')<{
  readonly method: 'createLiveViewServer';
}> {}

const liveViewServerErrorCauses = new WeakMap<LiveViewServerError, unknown>();

function liveViewServerFailure(cause: unknown): LiveViewServerError {
  const error = new LiveViewServerError({ method: 'createLiveViewServer' });
  liveViewServerErrorCauses.set(error, cause);
  return error;
}

export function liveViewServerErrorCause(error: LiveViewServerError): unknown {
  return liveViewServerErrorCauses.get(error);
}

const HEARTBEAT_MS = 15_000;
const MAX_SSE_CLIENTS = 8;

export interface LiveViewServerOptions {
  session: LiveViewSession;
  host?: string;
  port?: number;
}

export interface LiveViewServerAddress {
  host: string;
  shellPort: number;
  contentPort: number;
  shellOrigin: string;
  contentOrigin: string;
  url: string;
}

export interface LiveViewServer {
  readonly address: LiveViewServerAddress;
  close(): Promise<void>;
}

function hostForUrl(host: string): string {
  return host.includes(':') && !host.startsWith('[') ? `[${host}]` : host;
}

function authority(host: string, port: number): string {
  return `${hostForUrl(host)}:${port}`.toLowerCase();
}

function header(req: IncomingMessage, name: string): string | undefined {
  const value = req.headers[name];
  return Array.isArray(value) ? value[0] : value;
}

function requestAllowed(
  req: IncomingMessage,
  expectedAuthority: string,
  allowedOrigins: string[],
): boolean {
  if ((header(req, 'host') ?? '').trim().toLowerCase() !== expectedAuthority) return false;
  const origin = header(req, 'origin');
  return !origin || allowedOrigins.includes(origin);
}

function send(
  req: IncomingMessage,
  res: ServerResponse,
  status: number,
  body = '',
  headers: Record<string, string> = {},
): void {
  const bytes = Buffer.byteLength(body);
  res.writeHead(status, { 'content-length': String(bytes), ...headers });
  res.end(req.method === 'HEAD' ? undefined : body);
}

function securityHeaders(
  csp: string,
  options: { denyFraming?: boolean } = {},
): Record<string, string> {
  return {
    'content-security-policy': csp,
    'referrer-policy': 'no-referrer',
    'x-content-type-options': 'nosniff',
    ...(options.denyFraming === false ? {} : { 'x-frame-options': 'DENY' }),
    'x-robots-tag': 'noindex, nofollow',
    [LIVE_HTML_CONTRACT_HEADER]: LIVE_HTML_CONTRACT_VERSION,
  };
}

function nonce(): string {
  return randomBytes(18).toString('base64url');
}

function safeFilename(title: string): string {
  const stem = title
    .normalize('NFKD')
    .replace(/[^A-Za-z0-9 ._-]+/g, '')
    .replace(/\s+/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '')
    .slice(0, 72);
  return `${stem || 'okra-document'}.html`;
}

function encodeSse(event: LiveHtmlEvent): string {
  return `id: ${event.seq}\nevent: ${event.type}\ndata: ${JSON.stringify(event)}\n\n`;
}

function afterSequence(req: IncomingMessage, url: URL): number {
  const candidates = [url.searchParams.get('after'), header(req, 'last-event-id')];
  let after = 0;
  for (const candidate of candidates) {
    if (!candidate || !/^\d+$/.test(candidate)) continue;
    const value = Number(candidate);
    if (Number.isSafeInteger(value)) after = Math.max(after, value);
  }
  return after;
}

function localFrameScript(eventsPath: string): string {
  return `(() => {
    const root = document.getElementById('okra-doc');
    const phase = document.getElementById('okra-phase');
    if (!root || root.dataset.terminal === 'true') return;
    let seq = Number(root.dataset.seq || '0');
    let terminal = false;
    const source = new EventSource(${JSON.stringify(eventsPath)} + '?after=' + seq);
    const parse = (event, type) => {
      try {
        const value = JSON.parse(event.data || '{}');
        if (terminal || value.version !== ${JSON.stringify(LIVE_HTML_CONTRACT_VERSION)} || value.type !== type ||
            value.seq !== seq + 1 || String(value.seq) !== String(event.lastEventId)) throw new Error('contract');
        seq = value.seq;
        if (type === 'done' || type === 'error') terminal = true;
        return value;
      } catch (_) {
        source.close();
        terminal = true;
        if (phase) phase.textContent = 'Stream format error';
        return null;
      }
    };
    const element = (html, id) => {
      const template = document.createElement('template');
      template.innerHTML = String(html || '').trim();
      const node = template.content.firstElementChild;
      if (!node || template.content.childElementCount !== 1 || node.id !== id) return null;
      return node;
    };
    source.addEventListener('progress', event => {
      const value = parse(event, 'progress');
      if (value && phase) phase.textContent = value.phase;
    });
    source.addEventListener('append', event => {
      const value = parse(event, 'append');
      if (!value || document.getElementById(value.block_id)) return;
      const node = element(value.html, value.block_id);
      if (!node) return source.close();
      document.getElementById('okra-load')?.remove();
      root.appendChild(node);
    });
    source.addEventListener('replace', event => {
      const value = parse(event, 'replace');
      if (!value) return;
      const id = String(value.target || '').slice(1);
      const current = document.getElementById(id);
      const node = element(value.html, id);
      if (!current || !node) return source.close();
      current.replaceWith(node);
    });
    source.addEventListener('done', event => {
      if (!parse(event, 'done')) return;
      if (phase) phase.textContent = 'Ready';
      source.close();
    });
    source.addEventListener('error', event => {
      if (!event.data) return;
      const value = parse(event, 'error');
      if (!value) return;
      let error = document.getElementById('okra-error');
      if (!error) {
        error = document.createElement('p');
        error.id = 'okra-error';
        error.setAttribute('role', 'alert');
        root.appendChild(error);
      }
      error.textContent = value.message;
      if (phase) phase.textContent = 'Failed';
      source.close();
    });
  })();`;
}

function frameHtml(session: LiveViewSession, eventsPath: string, frameNonce: string): string {
  const snapshot = session.snapshot();
  const body = snapshot.blocks.length
    ? snapshot.blocks.map((block) => block.html).join('\n')
    : '<p id="okra-load">Preparing page stream…</p>';
  const terminal = snapshot.status === 'done' || snapshot.status === 'error';
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<meta name="robots" content="noindex"><title>${escapeHtml(session.title)}</title>
<style nonce="${frameNonce}">
:root{color-scheme:light;--paper:#fffef9;--ink:#1a1a15;--soft:#5f5b50;--line:#e7e1d3;--accent:#1f7a4c;--tint:#edf6ef}
*{box-sizing:border-box}body{margin:0;background:var(--paper);color:var(--ink);font:16px/1.65 ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif}
.status{position:sticky;top:0;z-index:2;padding:8px 20px;border-bottom:1px solid var(--line);background:color-mix(in srgb,var(--paper) 94%,transparent);color:var(--soft);font-size:12px;backdrop-filter:blur(8px)}
main{max-width:820px;margin:auto;padding:24px 26px 88px}section[data-page]{padding:8px 0 28px;margin:0 0 26px;border-bottom:1px solid var(--line)}
h1{font-size:2rem;line-height:1.18;letter-spacing:-.025em}h2{font-size:1.45rem;line-height:1.25}h3{font-size:1.15rem}h1,h2,h3,h4,h5,h6{margin:1.25em 0 .5em}p{margin:0 0 .85em}a{color:var(--accent)}
table{display:block;max-width:100%;overflow:auto;border-collapse:collapse;margin:1rem 0}th,td{border:1px solid var(--line);padding:.45rem .65rem;text-align:left;vertical-align:top}th{background:var(--tint)}
pre{max-width:100%;overflow:auto;padding:12px;border-radius:7px;background:var(--tint);white-space:pre-wrap}code{font-family:ui-monospace,SFMono-Regular,Menlo,monospace}figure{margin:1rem 0;padding:14px;border-left:3px solid var(--line);color:var(--soft)}img:not([src]){display:none}#okra-load{color:var(--soft)}#okra-error{padding:12px;border:1px solid #e5a7b4;border-radius:7px;background:#fff0f3;color:#92243a}
@media(max-width:600px){main{padding:18px 16px 64px}.status{padding:7px 14px}}
</style></head><body>
<div class="status" id="okra-phase" role="status" aria-live="polite">${escapeHtml(snapshot.phase)}</div>
<main id="okra-doc" data-seq="${snapshot.seq}" data-terminal="${terminal}">${body}</main>
<script nonce="${frameNonce}">${localFrameScript(eventsPath)}</script>
</body></html>`;
}

function shellHtml(session: LiveViewSession, frameUrl: string, shellNonce: string): string {
  const id = encodeURIComponent(session.id);
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<meta name="robots" content="noindex"><title>${escapeHtml(session.title)} · local okraPDF viewer</title>
<style nonce="${shellNonce}">
:root{color-scheme:light dark;--paper:#fffef9;--raised:#fff;--sunk:#f2efe6;--ink:#1a1a15;--soft:#686357;--line:#e6e0d2;--accent:#1f7a4c}
@media(prefers-color-scheme:dark){:root{--paper:#fffef9;--raised:#1b1b19;--sunk:#0d0d0c;--ink:#f3f2ee;--soft:#aaa69d;--line:#38372f;--accent:#6fc290}}
*{box-sizing:border-box}html,body{height:100%}body{margin:0;display:flex;flex-direction:column;background:var(--sunk);color:var(--ink);font:14px/1.4 ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif}
header{display:flex;align-items:center;gap:12px;min-height:54px;padding:8px 14px;border-bottom:1px solid var(--line);background:var(--raised)}
.brand{font-weight:750;letter-spacing:-.02em}.local{padding:4px 8px;border-radius:999px;background:color-mix(in srgb,var(--accent) 15%,transparent);color:var(--accent);font-size:11px;font-weight:700;text-transform:uppercase}.title{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:var(--soft)}
.spacer{flex:1}.download{padding:7px 10px;border:1px solid var(--line);border-radius:7px;color:var(--ink);text-decoration:none;background:var(--raised);white-space:nowrap}.download:hover{border-color:var(--accent)}
.disclosure{margin:0;padding:7px 14px;border-bottom:1px solid var(--line);background:var(--raised);color:var(--soft);font-size:12px}.disclosure strong{color:var(--ink)}
iframe{flex:1;width:100%;min-height:0;border:0;background:var(--paper)}
@media(max-width:620px){.title{display:none}.disclosure{font-size:11px}}
</style></head><body>
<header><span class="brand">okraPDF</span><span class="local">localhost</span><span class="title">${escapeHtml(session.title)}</span><span class="spacer"></span><a class="download" href="/s/${id}/download">Download HTML</a></header>
<p class="disclosure"><strong>Local viewer, BYOK processing:</strong> this server and webpage stay on your machine; the PDF is sent directly to Unstructured for conversion. No Okra account or telemetry.</p>
<iframe src="${escapeHtml(frameUrl)}" title="HTML view of ${escapeHtml(session.title)}" sandbox="allow-scripts allow-same-origin" referrerpolicy="no-referrer"></iframe>
</body></html>`;
}

function downloadHtml(session: LiveViewSession): string {
  const body = session
    .snapshot()
    .blocks.map((block) => block.html)
    .join('\n');
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="generator" content="okraPDF ${LIVE_HTML_CONTRACT_VERSION}"><title>${escapeHtml(session.title)}</title><style>body{max-width:820px;margin:40px auto;padding:0 24px;color:#1a1a15;background:#fffef9;font:16px/1.65 system-ui,sans-serif}section[data-page]{padding-bottom:26px;margin-bottom:26px;border-bottom:1px solid #e7e1d3}table{display:block;max-width:100%;overflow:auto;border-collapse:collapse}th,td{border:1px solid #e7e1d3;padding:7px 10px;text-align:left}pre{overflow:auto;white-space:pre-wrap}</style></head><body>${body}</body></html>`;
}

const listenEffect = Effect.fn('okrapdf/cli.liveView.listen')(function* (
  server: Server,
  port: number,
  host: string,
) {
  let cleanup = () => {};
  yield* Effect.callback<void, LiveViewServerError>((resume, signal) => {
    const onError = (cause: Error) => resume(Effect.fail(liveViewServerFailure(cause)));
    const onListening = () => resume(Effect.void);
    server.once('error', onError);
    server.once('listening', onListening);
    cleanup = () => {
      server.off('error', onError);
      server.off('listening', onListening);
    };
    try {
      server.listen({ port, host, signal });
    } catch (cause) {
      resume(Effect.fail(liveViewServerFailure(cause)));
    }
    return Effect.sync(cleanup);
  }).pipe(Effect.ensuring(Effect.sync(() => cleanup())));
  return (server.address() as AddressInfo).port;
});

const closeServerEffect = Effect.fn('okrapdf/cli.liveView.closeServer')(function* (server: Server) {
  if (!server.listening) return;
  yield* Effect.callback<void, LiveViewServerError>((resume) => {
    server.close((cause) =>
      resume(cause ? Effect.fail(liveViewServerFailure(cause)) : Effect.void),
    );
    server.closeAllConnections?.();
  });
});

/** Scope-owned variant for native CLI consumers. */
export const liveViewServerScopedEffect = Effect.fn('okrapdf/cli.liveView.scoped')(
  (
    options: LiveViewServerOptions,
  ): Effect.Effect<LiveViewServer, LiveViewServerError, Scope.Scope> =>
    Effect.uninterruptibleMask((restore) =>
      restore(createLiveViewServerEffect(options)).pipe(
        Effect.tap((server) => Effect.addFinalizer(() => Effect.promise(() => server.close()))),
      ),
    ),
);

export function createLiveViewServer(options: LiveViewServerOptions): Promise<LiveViewServer> {
  return Effect.runPromise(
    createLiveViewServerEffect(options).pipe(
      Effect.catch((error) => Effect.fail(liveViewServerErrorCause(error))),
    ),
  );
}

export const createLiveViewServerEffect = Effect.fn('okrapdf/cli.createLiveViewServer')(function* (
  options: LiveViewServerOptions,
): Effect.fn.Return<LiveViewServer, LiveViewServerError> {
  const requestedHost = options.host ?? '127.0.0.1';
  if (!isLoopbackHost(requestedHost)) {
    return yield* Effect.fail(
      liveViewServerFailure(new Error(`Refusing non-loopback host ${requestedHost}.`)),
    );
  }
  const normalizedHost = requestedHost
    .trim()
    .toLowerCase()
    .replace(/^\[|\]$/g, '');
  // Never trust local name resolution for the security boundary. A machine can
  // map `localhost` away from loopback in /etc/hosts; bind the literal address.
  const host = normalizedHost === 'localhost' ? '127.0.0.1' : normalizedHost;
  const session = options.session;
  let address: LiveViewServerAddress | undefined;
  const openResponses = new Set<ServerResponse>();

  const shellServer = createServer((req, res) => {
    if (!address) return send(req, res, 503, 'Starting');
    if (!requestAllowed(req, authority(host, address.shellPort), [address.shellOrigin])) {
      return send(req, res, 421, 'Misdirected request', { 'cache-control': 'no-store' });
    }
    const url = new URL(req.url ?? '/', address.shellOrigin);
    if (req.method !== 'GET' && req.method !== 'HEAD') {
      return send(req, res, 405, 'Method not allowed', { allow: 'GET, HEAD' });
    }
    if (url.pathname === '/') {
      return send(req, res, 404, 'Not found\n', {
        'content-type': 'text/plain; charset=utf-8',
        'cache-control': 'no-store',
      });
    }
    if (url.pathname === '/health') {
      return send(req, res, 200, 'ok\n', {
        'content-type': 'text/plain; charset=utf-8',
        'cache-control': 'no-store',
      });
    }
    if (url.pathname === `/s/${encodeURIComponent(session.id)}/download`) {
      const snapshot = session.snapshot();
      if (snapshot.status !== 'done') {
        return send(
          req,
          res,
          snapshot.status === 'error' ? 409 : 425,
          'The HTML document is not ready yet.\n',
          {
            'content-type': 'text/plain; charset=utf-8',
            'cache-control': 'no-store',
            ...(snapshot.status === 'error' ? {} : { 'retry-after': '2' }),
            [LIVE_HTML_CONTRACT_HEADER]: LIVE_HTML_CONTRACT_VERSION,
          },
        );
      }
      const html = downloadHtml(session);
      return send(req, res, 200, html, {
        'content-type': 'text/html; charset=utf-8',
        'content-disposition': `attachment; filename="${safeFilename(session.title)}"`,
        'cache-control': 'private, no-cache',
        ...securityHeaders(
          "default-src 'none'; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'; frame-ancestors 'none'",
        ),
      });
    }
    if (url.pathname !== `/s/${encodeURIComponent(session.id)}`) {
      return send(req, res, 404, 'Not found');
    }
    if (
      url.searchParams.get('format') === 'text' ||
      header(req, 'accept')?.includes('text/plain')
    ) {
      const snapshot = session.snapshot();
      const text = `# ${session.title}\n\n${snapshot.blocks
        .map((block) => htmlToText(block.html))
        .filter(Boolean)
        .join('\n\n')}\n`;
      return send(req, res, 200, text, {
        'content-type': 'text/plain; charset=utf-8',
        'cache-control': 'no-store',
      });
    }
    const shellNonce = nonce();
    const frameUrl = `${address.contentOrigin}/s/${encodeURIComponent(session.id)}/frame`;
    const html = shellHtml(session, frameUrl, shellNonce);
    return send(req, res, 200, html, {
      'content-type': 'text/html; charset=utf-8',
      'cache-control': 'no-store',
      ...securityHeaders(
        `default-src 'none'; style-src 'nonce-${shellNonce}'; frame-src ${address.contentOrigin}; base-uri 'none'; form-action 'none'; frame-ancestors 'none'`,
      ),
    });
  });

  const contentServer = createServer((req, res) => {
    if (!address) return send(req, res, 503, 'Starting');
    if (!requestAllowed(req, authority(host, address.contentPort), [address.contentOrigin])) {
      return send(req, res, 421, 'Misdirected request', { 'cache-control': 'no-store' });
    }
    const url = new URL(req.url ?? '/', address.contentOrigin);
    if (req.method !== 'GET' && req.method !== 'HEAD') {
      return send(req, res, 405, 'Method not allowed', { allow: 'GET, HEAD' });
    }
    const framePath = `/s/${encodeURIComponent(session.id)}/frame`;
    const eventPath = `/s/${encodeURIComponent(session.id)}/events`;
    if (url.pathname === framePath) {
      const frameNonce = nonce();
      const html = frameHtml(session, eventPath, frameNonce);
      return send(req, res, 200, html, {
        'content-type': 'text/html; charset=utf-8',
        'cache-control': 'no-store',
        ...securityHeaders(
          `default-src 'none'; script-src 'nonce-${frameNonce}'; style-src 'nonce-${frameNonce}'; connect-src 'self'; img-src 'self' data:; base-uri 'none'; form-action 'none'; frame-ancestors ${address.shellOrigin}`,
          { denyFraming: false },
        ),
      });
    }
    if (url.pathname !== eventPath) return send(req, res, 404, 'Not found');
    if (req.method === 'HEAD') {
      res.writeHead(200, {
        'content-type': 'text/event-stream; charset=utf-8',
        'cache-control': 'no-cache, no-transform',
        [LIVE_HTML_CONTRACT_HEADER]: LIVE_HTML_CONTRACT_VERSION,
      });
      return res.end();
    }

    if (openResponses.size >= MAX_SSE_CLIENTS) {
      return send(req, res, 503, 'Too many stream clients\n', {
        'content-type': 'text/plain; charset=utf-8',
        'cache-control': 'no-store',
        'retry-after': '2',
      });
    }

    res.writeHead(200, {
      'content-type': 'text/event-stream; charset=utf-8',
      'cache-control': 'no-cache, no-transform',
      connection: 'keep-alive',
      'x-accel-buffering': 'no',
      [LIVE_HTML_CONTRACT_HEADER]: LIVE_HTML_CONTRACT_VERSION,
    });
    res.write('retry: 2000\n: connected\n\n');
    openResponses.add(res);
    let last = afterSequence(req, url);
    let closed = false;
    let waitingForDrain = false;
    let queuedBytes = 0;
    const queuedEvents: LiveHtmlEvent[] = [];
    const MAX_QUEUED_SSE_BYTES = 2 * 1024 * 1024;
    let unsubscribe = () => {};
    let stopHeartbeat = () => {};
    const finish = (destroy = false): void => {
      if (closed) return;
      closed = true;
      stopHeartbeat();
      unsubscribe();
      openResponses.delete(res);
      if (destroy) res.destroy();
      else if (!res.writableEnded) res.end();
    };
    const finishIfTerminal = (event: LiveHtmlEvent): boolean => {
      if (event.type !== 'done' && event.type !== 'error') return false;
      finish();
      return true;
    };
    const flushQueued = (): void => {
      if (closed || waitingForDrain) return;
      while (queuedEvents.length) {
        const event = queuedEvents.shift()!;
        const frame = encodeSse(event);
        queuedBytes -= Buffer.byteLength(frame);
        last = event.seq;
        const accepted = res.write(frame);
        if (finishIfTerminal(event)) return;
        if (!accepted) {
          waitingForDrain = true;
          res.once('drain', () => {
            waitingForDrain = false;
            flushQueued();
          });
          return;
        }
      }
    };
    const writeEvent = (event: LiveHtmlEvent): void => {
      if (closed || event.seq <= last) return;
      if (queuedEvents.length && event.seq <= queuedEvents[queuedEvents.length - 1].seq) return;
      const frameBytes = Buffer.byteLength(encodeSse(event));
      if (waitingForDrain && queuedBytes + frameBytes > MAX_QUEUED_SSE_BYTES) return finish(true);
      queuedBytes += frameBytes;
      queuedEvents.push(event);
      flushQueued();
    };
    stopHeartbeat = Effect.runCallback(
      Effect.sync(() => {
        if (!closed && !waitingForDrain && !res.write(': hb\n\n')) {
          waitingForDrain = true;
          res.once('drain', () => {
            waitingForDrain = false;
            flushQueued();
          });
        }
      }).pipe(Effect.repeat(Schedule.spaced(HEARTBEAT_MS)), Effect.delay(HEARTBEAT_MS)),
    );
    unsubscribe = session.subscribe(writeEvent);
    for (const event of session.events(last)) writeEvent(event);
    req.once('close', finish);
    if (
      (session.snapshot().status === 'done' || session.snapshot().status === 'error') &&
      queuedEvents.length === 0 &&
      !waitingForDrain
    )
      finish();
  });

  const closeEffect = Effect.gen(function* () {
    const sessionExit = yield* Effect.exit(
      Effect.tryPromise({
        try: () => session.close(),
        catch: liveViewServerFailure,
      }),
    );
    for (const response of openResponses) response.end();
    openResponses.clear();
    // Attempt every cleanup even if session shutdown or one listener fails.
    const contentExit = yield* Effect.exit(closeServerEffect(contentServer));
    const shellExit = yield* Effect.exit(closeServerEffect(shellServer));
    if (Exit.isFailure(sessionExit)) return yield* Effect.failCause(sessionExit.cause);
    if (Exit.isFailure(contentExit)) return yield* Effect.failCause(contentExit.cause);
    if (Exit.isFailure(shellExit)) return yield* Effect.failCause(shellExit.cause);
  }).pipe(Effect.uninterruptible);
  let closePromise: Promise<void> | undefined;
  const close = () =>
    (closePromise ??= Effect.runPromise(
      closeEffect.pipe(Effect.catch((error) => Effect.fail(liveViewServerErrorCause(error)))),
    ));
  return yield* Effect.gen(function* () {
    const shellPort = yield* listenEffect(shellServer, options.port ?? 0, host);
    const contentPort = yield* listenEffect(contentServer, 0, host);
    const shellOrigin = `http://${authority(host, shellPort)}`;
    const contentOrigin = `http://${authority(host, contentPort)}`;
    address = {
      host,
      shellPort,
      contentPort,
      shellOrigin,
      contentOrigin,
      url: `${shellOrigin}/s/${encodeURIComponent(session.id)}`,
    };
    return { address, close };
  }).pipe(Effect.onError(() => closeEffect.pipe(Effect.catch(() => Effect.void))));
});

export function isLoopbackHost(host: string): boolean {
  const normalized = host
    .trim()
    .toLowerCase()
    .replace(/^\[|\]$/g, '');
  return normalized === '127.0.0.1' || normalized === '::1' || normalized === 'localhost';
}
